import { useEffect, useRef, useState } from 'react';
import type { UsageTerms } from '../types';

/**
 * 「投稿者以外の利用」条件を変更するモーダル。
 * 実装は PublishConfirmModal と同じ作法（Escape で閉じる・body スクロールロック等）を踏襲する。
 * 開いた時点の value を draft にコピーし、「変更する」で確定するまで外部の state は変更しない。
 */
interface UsageTermsModalProps {
  open: boolean;
  value: UsageTerms;
  onCancel: () => void;
  onConfirm: (next: UsageTerms) => void;
}

export default function UsageTermsModal({ open, value, onCancel, onConfirm }: UsageTermsModalProps) {
  const [draft, setDraft] = useState<UsageTerms>(value);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // 開くたびに、その時点の確定値へ draft をリセットする（前回の未確定編集を引きずらないため）。
  useEffect(() => {
    if (open) {
      setDraft(value);
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    confirmBtnRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const toggle = (key: 'violent' | 'sexual' | 'corporate' | 'redistribute' | 'modify') =>
    setDraft((d) => ({ ...d, [key]: !d[key] }));
  const toggleCommercial = () =>
    setDraft((d) => ({
      ...d,
      commercial: !d.commercial,
      commercialScope: !d.commercial ? (d.commercialScope ?? 'nonprofit') : undefined,
    }));

  const handleConfirm = () => {
    onConfirm(draft);
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="usage-terms-title">
      <div className="modal__backdrop" onClick={onCancel} />
      <div className="modal__panel usage-terms-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3 id="usage-terms-title" className="modal__title">
            利用条件を変更
          </h3>
          <button type="button" className="modal__close" onClick={onCancel} aria-label="閉じる">
            ×
          </button>
        </div>

        <p className="usage-terms-modal__lead">
          公開ポーズは誰でも閲覧でき、連携アプリでダウンロード・利用できます。以下で許可する利用条件を設定してください。
        </p>

        <div className="field usage-terms-modal__checklist">
          <label>ポーズデータを使って、他の人がやってよい事</label>
          <div className="radio-group">
            <label className="radio">
              <input type="checkbox" checked={draft.violent} onChange={() => toggle('violent')} />
              暴力表現での利用
            </label>
            <label className="radio">
              <input type="checkbox" checked={draft.sexual} onChange={() => toggle('sexual')} />
              性的表現での利用
            </label>
            <label className="radio">
              <input type="checkbox" checked={draft.corporate} onChange={() => toggle('corporate')} />
              法人利用
            </label>
            <label className="radio">
              <input type="checkbox" checked={draft.commercial} onChange={toggleCommercial} />
              個人の商用利用
            </label>
            {draft.commercial && (
              <div className="modal__nested radio-group">
                <label className="radio">
                  <input
                    type="radio"
                    name="commercial-scope"
                    checked={draft.commercialScope !== 'nonprofit'}
                    onChange={() => setDraft((d) => ({ ...d, commercialScope: 'profit' }))}
                  />
                  営利目的の活動（ギフティング等）OK
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name="commercial-scope"
                    checked={draft.commercialScope === 'nonprofit'}
                    onChange={() => setDraft((d) => ({ ...d, commercialScope: 'nonprofit' }))}
                  />
                  非営利目的の活動（同人活動等）に限る
                </label>
              </div>
            )}
            <label className="radio">
              <input type="checkbox" checked={draft.redistribute} onChange={() => toggle('redistribute')} />
              再配布
            </label>
            <label className="radio">
              <input type="checkbox" checked={draft.modify} onChange={() => toggle('modify')} />
              改変（ポーズの調整）
            </label>
            <label className="radio">
              <input
                type="checkbox"
                checked={!draft.creditRequired}
                onChange={() => setDraft((d) => ({ ...d, creditRequired: !d.creditRequired }))}
              />
              クレジット表記: 不要
            </label>
          </div>
        </div>

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel}>
            キャンセル
          </button>
          <button type="button" className="btn btn--primary" onClick={handleConfirm} ref={confirmBtnRef}>
            変更する
          </button>
        </div>
      </div>
    </div>
  );
}
